import CustomTooltip from '../../../../shared/CustomTooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Grid, InputLabel, Typography, useTheme } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Link } from 'react-router-dom';
import {
    useEditAssetMutation,
    useGetIndividualOwnerAssetsQuery,
} from '../../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../../shared/AuthRoles';
import FormAutocomplete from '../../../../shared/FormAutocomplete';
import FormMultiAutocomplete from '../../../../shared/FormMultiAutocomplete';
import toast from '../../../../shared/Toast';

const LinkCalendars = ({ existingModule }: { existingModule?: any }) => {
    const { control, setValue } = useFormContext();
    const [editAsset] = useEditAssetMutation();
    const loggedInUser = UserRole();
    const { data: allAssets } = useGetIndividualOwnerAssetsQuery({
        url: `owners/${loggedInUser?.company}/modules`,
        sort: 'name',
        status: 'published',
    });

    const filteredAssets = allAssets?.filter(
        (asset: any) => asset.type === 'property',
    );

    React.useEffect(() => {
        if (
            existingModule?.combinedModules?.length > 0 &&
            existingModule?.mainModule
        ) {
            setValue('linkType', 'combine');
        }
    }, [existingModule, setValue]);

    const disableOption = (asset: any) => {
        return existingModule
            ? asset.linkedModules.length > 0 ||
                  (asset.mainModule &&
                      asset.mainModule.id !== existingModule.id)
            : asset.linkedModules.length > 0 || asset.mainModule;
    };

    const unlinkFromMain = () => {
        const requestBody = {
            type: existingModule?.mainModule?.type,
            linkedModules: existingModule?.mainModule?.linkedModules.filter(
                (linkedModule: any) => linkedModule.id !== existingModule.id,
            ),
        };

        const data = [
            loggedInUser?.company,
            existingModule?.mainModule.id,
            requestBody,
        ];

        try {
            editAsset(data);
            toast.success(`Experience successfully unlinked`);
        } catch (error) {
            toast.error(`Failed to unlink the experience`);
        }
    };

    const theme = useTheme();

    return (
        <Grid sx={{ pt: 2 }}>
            <Grid
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Grid
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        paddingBottom: '10px',
                    }}
                >
                    {' '}
                    <Typography
                        component='h4'
                        color='textPrimary'
                        fontWeight='bold'
                    >
                        Connect Calendars
                    </Typography>
                    <CustomTooltip
                        title={
                            <Typography>
                                If you have multiple experiences that share the
                                same space, like one for a single activity and
                                another for the entire venue, you can link their
                                calendars. This ensures that when a reservation
                                is made for one experience, the dates are
                                automatically blocked on the linked experiences
                                to avoid double bookings.
                            </Typography>
                        }
                        placement='right'
                    >
                        <HelpOutlineIcon
                            sx={{
                                fontSize: '16px',
                            }}
                        />
                    </CustomTooltip>
                </Grid>

                <Grid container columns={25} sx={{ paddingBottom: '10px' }}>
                    <Grid item xs={25} lg={5}>
                        <InputLabel
                            htmlFor='linked-modules__input'
                            sx={{
                                mt: 1,
                            }}
                        >
                            Experience
                        </InputLabel>
                    </Grid>
                    <Grid
                        item
                        xs={25}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                        }}
                    >
                        <FormMultiAutocomplete
                            id='linked-modules__input'
                            name='linkedModules'
                            control={control}
                            options={
                                (existingModule
                                    ? filteredAssets?.filter(
                                          (asset: any) =>
                                              asset.id !== existingModule.id,
                                      )
                                    : filteredAssets
                                )?.map((asset: any) => ({
                                    value: { id: asset.id },
                                    label: disableOption(asset)
                                        ? `${asset.name} (unavailable)`
                                        : asset.name,
                                })) ?? []
                            }
                            getOptionDisabled={(option) => {
                                const asset = allAssets.find(
                                    (asset: any) =>
                                        asset.id === option.value.id,
                                );

                                if (!asset) return false;

                                return disableOption(asset);
                            }}
                            disabled={existingModule?.mainModule}
                        />
                        {existingModule?.mainModule && (
                            <>
                                <Typography
                                    sx={{
                                        color: theme.palette.warning.main,
                                    }}
                                >
                                    <span>
                                        This experience is connected to{' '}
                                        <Link
                                            to={`/viewExperiences/${existingModule.mainModule.type}/${existingModule?.mainModule.id}`}
                                            style={{
                                                color: theme.palette.textAccent
                                                    .main,
                                                textDecoration: 'underline',
                                            }}
                                        >
                                            {existingModule.mainModule.name}
                                        </Link>{' '}
                                        via a combined calendar.
                                    </span>
                                </Typography>
                                <Typography color={theme.palette.warning.main}>
                                    <span>
                                        If you want to link this experience to
                                        another experience, you first need to
                                        unlink it from '
                                        {existingModule?.mainModule?.name}' (ID:
                                        {existingModule?.mainModule?.id}). Click{' '}
                                        <span
                                            style={{
                                                cursor: 'pointer',
                                                color: theme.palette.textAccent
                                                    .main,
                                                textDecoration: 'underline',
                                            }}
                                            onClick={unlinkFromMain}
                                        >
                                            here
                                        </span>{' '}
                                        to unlink.
                                    </span>
                                </Typography>
                            </>
                        )}
                    </Grid>
                </Grid>
                <Grid container columns={25}>
                    <Grid item xs={25}>
                        <InputLabel
                            htmlFor='link-type__input'
                            sx={{
                                mt: 1,
                            }}
                        >
                            Connection Type{' '}
                            <CustomTooltip
                                title={
                                    <Typography>
                                        <ul>
                                            When linking a calendar, you can
                                            choose between two connection types:
                                            <li>
                                                Block: This option blocks
                                                availability on the linked
                                                calendar whenever a booking is
                                                made for this property. Guests
                                                will only have access to the
                                                facilities of the property they
                                                booked.
                                            </li>
                                            <li>
                                                Combine numbers: This option
                                                also blocks the linked calendar,
                                                but in addition, it merges the
                                                capacity of both properties —
                                                including people, rooms, and
                                                overnight availability. Guests
                                                will have access to facilities
                                                at both properties.
                                            </li>
                                        </ul>
                                    </Typography>
                                }
                                placement='right'
                            >
                                <HelpOutlineIcon
                                    sx={{
                                        fontSize: '16px',
                                    }}
                                />
                            </CustomTooltip>
                        </InputLabel>
                    </Grid>
                    <Grid
                        item
                        xs={25}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                        }}
                    >
                        <FormAutocomplete
                            id='link-type__input'
                            name='linkType'
                            control={control}
                            options={[
                                { value: 'block', label: 'Block' },
                                {
                                    value: 'combine',
                                    label: 'Combine',
                                },
                            ]}
                            disabled={existingModule?.mainModule}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default LinkCalendars;
